import type { NodeEntry } from 'slate';
import { Editor, Node, Path, Point } from 'slate';

import { Queries } from './queries-object';
import { parseLink } from '../../util';
import { getListMarkerText, isListItem, isListLevel, isOrderedListMarkerStyle } from '../internal';
import type { InsertCrossReferenceTarget } from '../internal';
import type {
  BookmarkEndElement,
  BookmarkStartElement,
  CrossReferenceElement,
  ListLevel,
  ListLevelDefinition,
  ParagraphElement,
} from '../types';
import { isBookmarkEndElement, isBookmarkStartElement } from '../types';

interface ResolvedReferenceTarget {
  getText: () => string | null;
  start: Point;
}

/** Same error text that Word uses */
const BOOKMARK_NOT_FOUND_TEXT = 'Error! Reference source not found.';

/** Word renders the "number" of an unnumbered paragraph (one with no list number) as "0". */
const UNNUMBERED_PARAGRAPH_NUMBER = '0';

const bookmarkQueries = {
  /**
   * Get the start point of a bookmark, or null if the bookmark is not in the document.
   */
  getBookmarkStart: (editor: Editor, bookmarkName: string): Point | null =>
    Queries.getBookmarkStartAndEnd(editor, bookmarkName, true).start,

  /**
   * Get the start and end points of a bookmark, or null if the bookmark is not in the document.
   */
  getBookmarkStartAndEnd: (editor: Editor, bookmarkName: string, startOnly = false) => {
    const toPoint = (path: Path) =>
      Queries.getSelectablePoint(editor, {
        point: { offset: 0, path: [...path, 0] },
      });

    const startEntry = Editor.nodes<BookmarkStartElement>(editor, {
      at: [],
      match: node => isBookmarkStartElement(node) && node.name === bookmarkName,
    }).next().value;
    if (!startEntry) return { end: null, start: null };
    const [startMarker, startPath] = startEntry;
    const start = toPoint(startPath);

    if (startOnly) return { end: null, start };

    const endEntry = Editor.nodes<BookmarkEndElement>(editor, {
      at: [],
      match: node => isBookmarkEndElement(node) && node.markerId === startMarker.markerId,
    }).next().value;
    if (!endEntry) return { end: null, start };
    const [, endPath] = endEntry;
    const end = toPoint(endPath);

    return { end, start };
  },

  /**
   * Get the text of a bookmark, or null if the bookmark is not in the document.
   */
  getBookmarkText: (editor: Editor, bookmarkName: string): string | null => {
    const { end, start } = Queries.getBookmarkStartAndEnd(editor, bookmarkName);
    if (!start || !end) return null;

    return Queries.string(editor, {
      at: { anchor: start, focus: end },
    });
  },

  /**
   * Get every bookmark start marker in the document, in document order.
   */
  getBookmarks: (editor: Editor): NodeEntry<BookmarkStartElement>[] => [
    ...Editor.nodes<BookmarkStartElement>(editor, {
      at: [],
      match: isBookmarkStartElement,
      voids: true,
    }),
  ],

  /**
   * Get updated text for cross-reference field based on its instruction type and switches.
   */
  getCrossReferenceText(editor: Editor, crossRef: CrossReferenceElement, fieldPath: Path): string {
    const parsedLink = parseLink(crossRef.href);
    const bookmarkName = parsedLink.type === 'bookmark' ? parsedLink.bookmark : '';

    if (!bookmarkName) return BOOKMARK_NOT_FOUND_TEXT;

    if (crossRef.instruction === 'NOTEREF') {
      return resolveNoteRefFieldValue(editor, crossRef, bookmarkName);
    }

    const resolvedTarget = resolveBookmarkTarget(editor, bookmarkName);
    if (!resolvedTarget) return BOOKMARK_NOT_FOUND_TEXT;

    const fieldPoint = Editor.start(editor, fieldPath);
    return crossRef.instruction === 'REF'
      ? resolveRefFieldValue(editor, resolvedTarget, crossRef.switches, fieldPoint)
      : resolvePageRefFieldValue(editor, resolvedTarget, crossRef.switches, fieldPoint);
  },

  /**
   * Whether a cross-reference target is a numbered (ordered list) item — i.e.
   * whether a paragraph/heading-number reference to it resolves to a real
   * number rather than the "0" placeholder.
   */
  getIsNumberedTarget(editor: Editor, target: InsertCrossReferenceTarget): boolean {
    const resolvedTarget = resolveInsertTarget(editor, target);
    if (!resolvedTarget) return false;

    const paragraphEntry = Queries.paragraphAtPoint(editor, { at: resolvedTarget.start });
    const listProps = paragraphEntry && Queries.renderedParagraphListProps(paragraphEntry[0]);
    if (!listProps) return false;

    const { listDefinitions } = Queries.getNumbering(editor);
    const levelDefinition = listDefinitions[listProps.listId]?.[listProps.listLevel];
    return Boolean(levelDefinition && isOrderedListMarkerStyle(levelDefinition.style.name));
  },

  previewCrossReferenceText(
    editor: Editor,
    options: {
      fieldPoint: Point;
      instruction: Exclude<CrossReferenceElement['instruction'], 'NOTEREF'>;
      switches: string;
      target: InsertCrossReferenceTarget;
    }
  ): string {
    const { fieldPoint, instruction, switches, target } = options;
    const resolvedTarget = resolveInsertTarget(editor, target);
    if (!resolvedTarget) return BOOKMARK_NOT_FOUND_TEXT;

    return instruction === 'REF'
      ? resolveRefFieldValue(editor, resolvedTarget, switches, fieldPoint)
      : resolvePageRefFieldValue(editor, resolvedTarget, switches, fieldPoint);
  },
};

export default bookmarkQueries;

function resolveBookmarkTarget(
  editor: Editor,
  bookmarkName: string
): ResolvedReferenceTarget | null {
  const start = Queries.getBookmarkStart(editor, bookmarkName);
  if (!start) return null;
  return { getText: () => Queries.getBookmarkText(editor, bookmarkName), start };
}

function resolveInsertTarget(
  editor: Editor,
  target: InsertCrossReferenceTarget
): ResolvedReferenceTarget | null {
  if (target.kind === 'bookmark') return resolveBookmarkTarget(editor, target.name);

  if (!Node.has(editor, target.path)) return null;
  const start = Editor.start(editor, target.path);
  return { getText: () => Queries.getNodeText(editor, target.path), start };
}

/**
 * Resolve the field value for a REF field.
 *
 * REF inserts the text represented by the bookmark.
 * - `\n` switch: displays the paragraph number at the current level only.
 * - `\r` switch: displays the paragraph number in relative context.
 * - `\w` switch: displays the paragraph number in full context.
 * - `\t` switch: suppresses non-delimiter/non-numerical text (used with \n, \r, \w).
 * - `\p` switch: appends relative position ("above" or "below").
 * - `\h` switch: display-only (hyperlink), does not affect text.
 */
function resolveRefFieldValue(
  editor: Editor,
  target: ResolvedReferenceTarget,
  switches: string,
  fieldPoint: Point
): string {
  const { start } = target;
  const appendRelativePosition = switches.includes('\\p');
  const hasNumberingSwitch =
    switches.includes('\\n') || switches.includes('\\r') || switches.includes('\\w');

  let result: string;

  if (hasNumberingSwitch) {
    const paragraphEntry = Queries.paragraphAtPoint(editor, { at: start });
    if (!paragraphEntry) return BOOKMARK_NOT_FOUND_TEXT;

    const [paragraph, paragraphPath] = paragraphEntry;
    const activeListProps = Queries.renderedParagraphListProps(paragraph);

    if (activeListProps && isListItem(paragraph)) {
      const listItemPathArr = Queries.getListItemPath(
        editor,
        paragraphPath,
        activeListProps.listId
      );

      const formatted = listItemPathArr
        ? formatParagraphNumber(editor, paragraphEntry, listItemPathArr, switches, fieldPoint)
        : null;

      result = formatted ?? UNNUMBERED_PARAGRAPH_NUMBER;
    } else {
      result = UNNUMBERED_PARAGRAPH_NUMBER;
    }
  } else {
    const bookmarkText = target.getText();
    if (bookmarkText === null) return BOOKMARK_NOT_FOUND_TEXT;
    result = bookmarkText;
  }

  if (appendRelativePosition) {
    const position = getRelativePosition(fieldPoint, start);

    result = hasNumberingSwitch ? `${result} ${position}` : position;
  }

  return result;
}

/**
 * Resolve the field value for a PAGEREF field.
 *
 * PAGEREF inserts the page number containing the bookmark.
 * - `\p` switch: if same page, returns "above"/"below"; otherwise "on page #".
 * - `\h` switch: display-only (hyperlink), does not affect text.
 */
function resolvePageRefFieldValue(
  editor: Editor,
  target: ResolvedReferenceTarget,
  switches: string,
  fieldPoint: Point
): string {
  const { start } = target;
  const useRelativePosition = switches.includes('\\p');

  const bookmarkPageNumber = Queries.getPageNumberForCMPoint(editor, start);
  if (bookmarkPageNumber === null) return BOOKMARK_NOT_FOUND_TEXT;

  if (useRelativePosition) {
    const fieldPageNumber = Queries.getPageNumberForCMPoint(editor, fieldPoint);

    if (fieldPageNumber !== null && fieldPageNumber === bookmarkPageNumber) {
      return getRelativePosition(fieldPoint, start);
    }

    return `on page ${bookmarkPageNumber}`;
  }

  return String(bookmarkPageNumber);
}

/**
 * Resolve the field value for a NOTEREF field.
 *
 * NOTEREF inserts the footnote/endnote mark number for the bookmarked note.
 * Footnotes/endnotes are not currently supported in the editor, so this
 * preserves the existing field text or returns an error if the bookmark is missing.
 * - `\p` switch appends relative position ("above" or "below").
 * - `\f` switch relates to formatting, not supported yet.
 * - `\h` switch: display-only (hyperlink), does not affect text.
 */
function resolveNoteRefFieldValue(
  editor: Editor,
  crossRef: CrossReferenceElement,
  bookmarkName: string
): string {
  const bookmarkStart = Queries.getBookmarkStart(editor, bookmarkName);
  if (!bookmarkStart) return BOOKMARK_NOT_FOUND_TEXT;

  // Preserve existing text since footnotes/endnotes are not supported
  // TODO: See https://linear.app/aline/issue/EDI-9/footnotes
  return crossRef.children[0]?.text ?? '';
}

/**
 * Determine the relative position of `fieldPoint` to `bookmarkPoint`.
 * Returns "above" if the field appears after the bookmark (the bookmark is above),
 * and "below" if the field appears before the bookmark (the bookmark is below).
 */
function getRelativePosition(fieldPoint: Point, bookmarkPoint: Point): 'above' | 'below' {
  return Point.isBefore(fieldPoint, bookmarkPoint) ? 'below' : 'above';
}

/**
 * Check whether a level's customText pattern already references all levels
 * from %1 through %{listLevel+1}.
 */
function customTextCoversAllLevels(customText: string, listLevel: ListLevel): boolean {
  for (let i = 1; i <= listLevel + 1; i++) {
    if (!customText.includes(`%${i}`)) return false;
  }
  return true;
}

/** A level's own marker pattern, falling back to the default `%N.` when unset. */
function levelCustomText(levelDefinition: ListLevelDefinition, listLevel: number): string {
  return levelDefinition.style.customText ?? `%${listLevel + 1}.`;
}

/**
 * Number of leading levels that the field's own paragraph shares with the
 * target — the levels Word's `\r` (relative context) drops.
 *
 * Returns 0 (→ full context, identical to `\w`) when the field is not a
 * numbered item, is in a different list than the target, or shares no ancestor.
 * Two paragraphs in the same `listId` share the same ancestor item at level `i`
 * iff their per-level counter arrays are equal at every index `<= i`, so we
 * count the longest common leading prefix. The result is capped so the target's
 * own deepest level is always kept.
 */
function getRelativeContextStartLevel(
  editor: Editor,
  fieldPoint: Point,
  targetListId: string,
  targetListItemPath: Path
): number {
  // A top-level target has no ancestor levels to drop; skip the field lookup.
  if (targetListItemPath.length <= 1) return 0;

  const fieldParagraphEntry = Queries.paragraphAtPoint(editor, { at: fieldPoint });
  if (!fieldParagraphEntry) return 0;

  const [fieldParagraph, fieldParagraphPath] = fieldParagraphEntry;
  const fieldListProps = Queries.renderedParagraphListProps(fieldParagraph);
  if (!fieldListProps || fieldListProps.listId !== targetListId) return 0;

  const fieldListItemPath = Queries.getListItemPath(editor, fieldParagraphPath, targetListId);
  if (!fieldListItemPath) return 0;

  const commonPrefix = Path.common(fieldListItemPath, targetListItemPath).length;
  return Math.min(commonPrefix, targetListItemPath.length - 1);
}

/**
 * Build a paragraph number, dropping `startLevel` leading levels.
 *
 * With `startLevel` 0 this renders the full number (used by `\w`); with a
 * positive `startLevel` it renders the relative-context number (`\r`).
 *
 * Two marker shapes are handled:
 * - Per-level markers (the default, e.g. "%1.", "%2.", "%3." — each level
 *   renders only its own number): concatenate each level's own marker,
 *   starting at the effective start level. Concatenation also skips ancestor
 *   levels already embedded in a deeper level's own marker (e.g. legal
 *   "Section %1.%2" embeds the article number `%1`), so a self-contained
 *   intermediate level is not wrongly prefixed with "Article I.".
 * - A single self-contained deepest marker that embeds every level (e.g.
 *   DECIMAL_SECTION "%1.%2.%3."): render that one marker, masking the
 *   placeholders for the dropped leading levels.
 */
function buildContextNumber(
  editor: Editor,
  listId: string,
  listItemPath: Path,
  listLevel: ListLevel,
  startLevel = 0
): string {
  const { listDefinitions } = Queries.getNumbering(editor);
  const listDefinition = listDefinitions[listId];
  if (!listDefinition) return '';

  const levelDefinition = listDefinition[listLevel];
  if (!levelDefinition) return '';

  const deepestCustomText = levelCustomText(levelDefinition, listLevel);

  // The deepest level's own marker embeds every level: render it as one
  // self-contained marker, masking the dropped leading levels for `\r`.
  if (customTextCoversAllLevels(deepestCustomText, listLevel)) {
    const definition: ListLevelDefinition =
      startLevel <= 0
        ? levelDefinition
        : {
            ...levelDefinition,
            style: {
              ...levelDefinition.style,
              customText: removeLeadingPlaceholders(deepestCustomText, startLevel),
            },
          };
    return getListMarkerText(definition, { listLevel, listPath: listItemPath });
  }

  // Skip ancestor levels already embedded in a deeper level's own marker, but
  // never render fewer levels than the relative-context start requested.
  let embeddedStart = 0;
  for (let i = 0; i <= listLevel; i++) {
    if (!isListLevel(i)) break;
    const lvlDef = listDefinition[i];
    if (!lvlDef) continue;

    const safeCustomText = levelCustomText(lvlDef, i);
    if (customTextCoversAllLevels(safeCustomText, i)) embeddedStart = i;
  }
  const effectiveStart = Math.max(embeddedStart, startLevel);

  let result = '';
  for (let i = effectiveStart; i <= listLevel; i++) {
    if (!isListLevel(i)) break;
    const lvlDef = listDefinition[i];
    if (!lvlDef) continue;

    result += getListMarkerText(lvlDef, {
      listLevel: i,
      listPath: listItemPath,
    });
  }
  return result;
}

/**
 * Drop the `%1`…`%{maskCount}` placeholders (and their trailing separators)
 * from a self-contained marker pattern, keeping `%{maskCount+1}` onward — e.g.
 * "%1.%2.%3." with `maskCount` 1 becomes "%2.%3.".
 *
 * Only applied to purely numeric patterns; a pattern containing literal words
 * (e.g. "Section %1.%2") can't be meaningfully split, so it is returned
 * unchanged and relative context degrades to full context for that marker.
 */
function removeLeadingPlaceholders(customText: string, maskCount: number): string {
  if (maskCount <= 0 || /[A-Za-z]/.test(customText)) return customText;

  const matches = [...customText.matchAll(/%(\d+)/g)];
  for (const match of matches) {
    const level = Number(match[1]);
    if (level > maskCount && typeof match.index === 'number') {
      return customText.slice(match.index);
    }
  }
  return customText;
}

/**
 * Format a paragraph number based on the REF field switch.
 *
 * - `\n`: The paragraph number at the current level only (no parent levels).
 * - `\r`: The paragraph number in relative context — the levels the field's own
 *   paragraph shares with the target are dropped. When the field shares no
 *   numbering ancestor with the target (e.g. it sits in ordinary body text, or
 *   in a different list), this resolves to the full number, matching `\w`.
 * - `\w`: The paragraph number in full context from anywhere in the document.
 * - `\t`: Suppresses non-delimiter/non-numerical text from the result.
 */
function formatParagraphNumber(
  editor: Editor,
  paragraphEntry: NodeEntry<ParagraphElement>,
  listItemPath: Path,
  switches: string,
  fieldPoint: Point
): string | null {
  const [paragraph] = paragraphEntry;
  const activeListProps = Queries.renderedParagraphListProps(paragraph);
  if (!activeListProps) return null;

  const suppressNonDelimiter = switches.includes('\\t');
  const isNumberNoContext = switches.includes('\\n');
  const isNumberRelativeContext = switches.includes('\\r');
  const isNumberFullContext = switches.includes('\\w');

  const { listDefinitions } = Queries.getNumbering(editor);
  const listDefinition = listDefinitions[activeListProps.listId];
  if (!listDefinition) return null;

  const levelDefinition = listDefinition[activeListProps.listLevel];
  if (!levelDefinition) return null;

  // A bullet/symbol marker has no number; let the caller render Word's "0".
  if (!isOrderedListMarkerStyle(levelDefinition.style.name)) return null;

  let result: string;

  if (isNumberFullContext || isNumberRelativeContext) {
    // `\r` drops the leading levels the field's own paragraph shares with the
    // target; `\w` always renders the full number (start level 0).
    const startLevel =
      isNumberRelativeContext && !isNumberFullContext
        ? getRelativeContextStartLevel(editor, fieldPoint, activeListProps.listId, listItemPath)
        : 0;

    result = buildContextNumber(
      editor,
      activeListProps.listId,
      listItemPath,
      activeListProps.listLevel,
      startLevel
    );
  } else if (isNumberNoContext) {
    result = getListMarkerText(levelDefinition, {
      listLevel: activeListProps.listLevel,
      listPath: listItemPath,
    });
  } else {
    return null;
  }

  if (suppressNonDelimiter) {
    result = result.replace(/[^0-9a-zA-Z.]+$/g, '').replace(/\.$/g, '');
  } else {
    result = result.replace(/\.$/g, '');
  }

  return result;
}
